import React from 'react';
import Img from 'react-image';
import get from 'lodash.get';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FlexBox,
  Typography,
  Card,
  color,
  Checkbox,
  Grid,
  useMediaQuery,
  useTheme,
  SvgIcon
} from '@kis-technologies/kis-ui';
import {
  selectors as configSelectors,
  Configuration,
  EventDisplaySettings,
  LanguageField
} from 'services/configuration';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { formatCurrency } from 'utils/stringFormat';
import { getEventDisplaySettings } from 'views/Ticketing/utils';
import { useStyles } from '../styled';
import { EventTiersProps } from '../types';

export const EventTiers = (props: EventTiersProps) => {
  const { event, tiers, tierSelection, tierSelectionKey, getImageUrl, onTileClick } = props;
  const { t, i18n } = useTranslation();
  const configuration = useSelector(configSelectors.selectConfiguration) as Configuration;
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);
  const showPriceRange = get(configuration, `${ticketingPath}.showPriceRange`);
  const availabilityConfig = get(configuration, `${ticketingPath}.eventTierAvailability`);
  const lowAvailabilityThreshold: number | undefined = get(availabilityConfig, 'lowAvailabilityThreshold');
  const soldOutThreshold = get(availabilityConfig, 'soldOutThreshold', 0);
  const showSoldOut = get(availabilityConfig, 'showSoldOut');
  const normalAvailabilityText = get(availabilityConfig, `messages.normal.${i18n.language}`, '');
  const lowAvailabilityText: string = get(availabilityConfig, `messages.lowAvailability.${i18n.language}`, '');
  const soldOutText = get(availabilityConfig, `messages.soldOut.${i18n.language}`);
  const unlimitedAvailabilityText = get(availabilityConfig, `messages.unlimited.${i18n.language}`, '');
  const languageSettings: LanguageField[] = get(configuration, 'configuration.languageSettings', []);
  const primaryPalette = get(configuration, 'configuration.globalSettings.palette.primary');
  const eventDisplaySettingsList: EventDisplaySettings[] = get(
    configuration,
    `${ticketingPath}.eventDisplaySettingsList`,
    []
  );
  const { showDate, showTime, showLocation } = getEventDisplaySettings(eventDisplaySettingsList, event.name);
  const expandedTierColumns =
    lowAvailabilityThreshold || normalAvailabilityText || unlimitedAvailabilityText || showPriceRange ? true : false;
  const fontFamily = get(configuration, 'configuration.globalSettings.fonts.primary');
  const classes = useStyles({ fontFamily })();
  const theme = useTheme();
  const matchPortrait = useMediaQuery(theme.breakpoints.down('lg'));
  // # of tier columns will vary based on config, orientation, image
  // landscape: 2, 5 or 6 columns; portrait: 1 or 3 columns;
  let tierColumns = getImageUrl(event.name, event.venue) ? 5 : 6;
  if (matchPortrait) {
    tierColumns = expandedTierColumns ? 1 : 3;
  } else if (expandedTierColumns) {
    tierColumns = 2;
  }

  const hasValidTiers = Object.values(tiers).some(
    (curTier) => !(soldOutThreshold ? curTier.tierAvailability <= soldOutThreshold : curTier.tierAvailability === 0)
  );

  const getAvailabilityMessage = (availCount: number): string => {
    let availabilityMessage = '';

    // if -1: assume that means unlimited, use the unlimited message config
    if (availCount === -1) {
      availabilityMessage = unlimitedAvailabilityText;
      // else if greater than 0 and less than a configured threshold: use low availability message config or localized text
    } else if (availCount > 0 && lowAvailabilityThreshold && availCount <= lowAvailabilityThreshold) {
      availabilityMessage = lowAvailabilityText
        ? lowAvailabilityText.replace('{{count}}', availCount.toString())
        : t('tierSelection.lowAvailability', { count: availCount });
      // if greater than 0: use the normal message config
    } else if (availCount > 0) {
      availabilityMessage = normalAvailabilityText.replace('{{count}}', availCount.toString());
    }

    return availabilityMessage;
  };

  return (
    <Box flexOverride="none">
      <Grid container className={classes.eventTierCardContent}>
        {getImageUrl(event.name, event.venue) && (
          <Grid className={classes.eventTierCardImage}>
            <Img src={getImageUrl(event.name, event.venue)} className={classes.eventTierImage} />
          </Grid>
        )}
        <Grid className={classes.eventTierDataContainer}>
          <Grid className={classes.eventTierCardData}>
            {event && (
              <>
                <Typography className={classes.eventTierCardHeading}>{event.name}</Typography>
                {event.date && (
                  <Typography className={classes.eventTierCardSubHead}>
                    {showDate ? t('{{date, weekdayMonthDay}}', { date: new Date(event.date) }) : ''}
                    {showDate && showTime ? <> &middot; </> : null}
                    {showTime ? t('{{date, time}}', { date: new Date(event.date) }) : ''}
                    {(showDate || showTime) && showLocation && event.venue ? <> &middot; </> : null}
                    {showLocation && event.venue ? event.venue : ''}
                  </Typography>
                )}
                <Typography className={classes.eventTierCardBody}>{event.description}</Typography>
              </>
            )}
          </Grid>
          {hasValidTiers || showSoldOut ? (
            <Grid
              container
              className={classes.tierCheckBoxContainer}
              sx={{
                gridTemplateColumns: `repeat(${tierColumns}, 1fr)`
              }}
            >
              {Object.keys(tiers).map((tier, idx) => {
                const { tierAvailability, minPrice, maxPrice, hasPromoOffer } = tiers[tier];
                const isSelected = tierSelection[tierSelectionKey] && tierSelection[tierSelectionKey].tier === tier;
                const isSoldOut: boolean = soldOutThreshold
                  ? tierAvailability <= soldOutThreshold
                  : tierAvailability === 0;
                let priceRange = formatCurrency(minPrice, {}, languageSettings);
                if (minPrice !== maxPrice)
                  priceRange = `${priceRange} - ${formatCurrency(maxPrice, {}, languageSettings)}`;

                // Tier Card
                return (
                  <>
                    {isSoldOut && !showSoldOut ? null : (
                      <Card
                        fullHeight
                        hasBorder={isSelected}
                        borderColor={primaryPalette}
                        key={idx}
                        disabled={isSoldOut}
                        sx={{ boxShadow: 'none' }}
                      >
                        <Card.ActionArea onClick={isSoldOut ? undefined : () => onTileClick(tier)}>
                          <FlexBox
                            border={`solid 1px ${color.LIGHT_GRAY}`}
                            padding="2 4"
                            align="center"
                            justify="between"
                            height="100%"
                            sx={{ minHeight: '80px' }}
                          >
                            <FlexBox flexOverride="1">
                              <Checkbox
                                size="medium"
                                label={
                                  <Grid className={classes.tierSelectionLabelText}>
                                    <Typography>{tier}</Typography>
                                  </Grid>
                                }
                                checked={isSelected ? true : false}
                                disabled={isSoldOut}
                                sx={{ width: 'unset' }}
                              />
                            </FlexBox>
                            {hasPromoOffer && (
                              <FlexBox align="center" justify="end" margin="0 5" data-testid={`tier-promo-icon-${idx}`}>
                                <SvgIcon name="success_circle" color="#64af13" size={60} />
                              </FlexBox>
                            )}
                            {!isSoldOut && (
                              <>
                                {(lowAvailabilityThreshold || normalAvailabilityText || unlimitedAvailabilityText) && (
                                  <Typography fontSize={16} fontWeight={700} sx={{ width: 150, marginLeft: '32px' }}>
                                    {getAvailabilityMessage(tierAvailability)}
                                  </Typography>
                                )}
                                {showPriceRange && (
                                  <>
                                    <Box
                                      width={2}
                                      margin="0 6"
                                      fullHeight
                                      sx={{ backgroundColor: color.X_LIGHT_GRAY }}
                                    />
                                    <Typography fontSize={24} fontWeight={700} sx={{ maxWidth: 100 }}>
                                      {priceRange}
                                    </Typography>
                                  </>
                                )}
                              </>
                            )}
                            {isSoldOut && (
                              <FlexBox
                                flexOverride={expandedTierColumns ? '0 1 auto' : '1 1 auto'}
                                hasBorderRadius
                                height={50}
                                sx={{ backgroundColor: color.RED }}
                                align="center"
                                justify="center"
                                padding="0 4"
                              >
                                <Typography color="WHITE" fontSize={18} fontWeight={900}>
                                  {soldOutText || t('tierSelection.soldOut')}
                                </Typography>
                              </FlexBox>
                            )}
                          </FlexBox>
                        </Card.ActionArea>
                      </Card>
                    )}
                  </>
                );
              })}
            </Grid>
          ) : (
            <FlexBox justify="center" align="center" height="150px">
              <Typography align="center" fontSize={32}>
                {t('tierSelection.ticketsNotAvailable')}
              </Typography>
            </FlexBox>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
